import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "src/styles/slick.css";
import "src/styles/slick-theme.css";
import { Link, useLocation } from "react-router-dom";
import Body from "src/components/common/Body";
import styled from "styled-components";
import Box from "src/components/common/Box";
import Button from "src/components/common/Button";
import RejectionReason from "src/components/as/RejectionReason";
import { useReportApproveMutation, useReportApproveRejectMutation, useReportDetailQuery } from "src/api/reportAPI";
import { getPathValue } from "src/utils/queryStringUtil";
import { useAppSelector } from "src/store/actions/action";
import { isEmpty } from "lodash";
import queryString from "query-string";
import asDetail from "./asDetail";
import moment from "moment";
import ImageModal, { RefProps } from "src/components/common/ImageModal";
import VideoBox from "src/components/common/VideoBox";
import ErrorParser from "src/utils/jsonParser";

const imageServerIp = process.env.REACT_APP_IMAGE_SERVER_IP;

function reportDetail() {
  const navigate = useNavigate();
  const { search, state: propsState } = useLocation();
  const queryParser = queryString.parse(search);
  const rnData = queryParser["rn"];

  const as_idx = getPathValue();
  const userInfo = useAppSelector((state) => state.userReduce.userInfo);

  // 초기 변수 선언
  const [state, setState] = useState<any>({
    reportDetailData: [],
    rejectReason: "",
    isRetry: false,
    isComplete: false,
    userType: userInfo.user_type,
    progress: "",
  });

  const { reportDetailData, isRetry, rejectReason, isComplete, userType, progress } = state;

  // PC/모바일 반응형 상태
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth <= 768;
    }
    return true;
  });

  // PC/모바일 반응형 감지
  const checkMobile = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [checkMobile]);

  const [navigateStateData, setNavigateStateData] = useState<any>(propsState);

  const [deleteYn, setDeleteYn] = useState<string>();

  const [esCode, setEsCode] = useState<string>();

  // API 영역
  const reportDetailAPI = useReportDetailQuery(as_idx);
  const [approveRejectUpdate, { isError: approveRejectIsError, isSuccess: approveRejectIsSuccess }] = useReportApproveRejectMutation();
  const [completeUpdate, { isError: completeIsError, isSuccess: completeIsSuccess, error: completeError, status: completeStatus }] = useReportApproveMutation();

  // hook 영역
  useEffect(() => {
    if (reportDetailAPI.isSuccess) {
      setState((prevState: any) => ({
        ...prevState,
        reportDetailData: reportDetailAPI.data,
        progress: reportDetailAPI.data.progress,
        rejectReason: reportDetailAPI.data.reject_reason,
      }));
    }
  }, [reportDetailAPI]);

  useEffect(() => {
    if (!isEmpty(navigateStateData)) {
      setDeleteYn(navigateStateData.deleteYn);
      setEsCode(navigateStateData.esCode);
    }
  }, [navigateStateData]);

  //  API 호출 (state 값 변경시)
  useEffect(() => {
    if (isComplete) {
      if (esCode === "EAC_100") {
        completeUpdate(as_idx);
      } else {
        alert("견적서가 완료처리 되지 않았습니다.");
        setState((prevState: any) => ({
          ...prevState,
          isComplete: false,
        }));
      }
    }
  }, [isComplete, esCode, as_idx, completeUpdate]);

  // 보완요청 업데이트 완료시
  useEffect(() => {
    if (approveRejectIsSuccess) {
      navigate("/as-detail/" + as_idx);
    }
  }, [approveRejectIsSuccess]);

  // 완료보고 승인 성공시
  useEffect(() => {
    if (completeIsSuccess) {
      navigate("/as-detail/" + as_idx);
    }
  }, [completeIsSuccess]);

  useEffect(() => {
    if (completeIsError) {
      const message = ErrorParser(completeError);
      alert(message);
    }
  }, [completeIsError]);

  const onChange = (e: any) => {
    const { value, id } = e.target;
    setState({
      ...state,
      [id]: value,
    });
  };

  // 퍼블리싱 영역
  const imageModalRef = React.useRef<RefProps>(null);
  const [nav1, setNav1] = useState();
  const [nav2, setNav2] = useState();
  const slider1 = React.useRef(null);
  const slider2 = React.useRef(null);

  useEffect(() => {
    if (slider1.current !== null && slider2.current !== null) {
      setNav1(slider1.current);
      setNav2(slider2.current);
    }
  }, [reportDetailData]);

  const settingsNav1 = {
    ref: slider1,
    asNavFor: nav2,
    dots: true,
    infinite: false,
    speed: 100,
    fade: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: !isMobile,
  };

  const settingsNav2 = {
    ref: slider2,
    asNavFor: nav1,
    useTransform: false,
    dots: true,
    infinite: false,
    speed: 100,
    fade: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    cssEase: "ease-out",
  };

  const openImageSlider = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (imageModalRef.current) imageModalRef.current.modalOpen();
  };

  const backClick = () => {
    if (!isEmpty(rnData)) {
      navigate("/as-detail/" + as_idx);
    } else {
      navigate(-1);
    }
  };

  const reCompleteBtn = () => {
    if (isEmpty(rejectReason)) {
      alert("보완요청 사유를 작성해 주세요");
      return;
    }
    const params = {
      as_idx: as_idx,
      reject_reason: rejectReason,
    };
    approveRejectUpdate(params);
  };

  return (
    <>
      <header>
        <div>
          <Button variant="text" className="btn-back" onClick={() => backClick()}>
            완료보고
          </Button>
        </div>
      </header>

      <Body type="detail">
        <ResponsiveContainer $isMobile={isMobile}>
          <FlexWrap $isMobile={isMobile}>
            {userInfo.user_type === "M" && rejectReason !== null && progress !== "ASC_100" && (
              <RejectionReason value={rejectReason} readonly title="보완요청 사유" />
            )}

            {/* PC: 2컬럼 레이아웃 */}
            <MainContentArea $isMobile={isMobile}>
              {/* 이미지 슬라이더 영역 */}
              <ImageSection $isMobile={isMobile}>
                {!isEmpty(reportDetailData) && (
                  <>
                    {reportDetailData.images.length > 0 && (
                      <ImgSlide $isMobile={isMobile}>
                        <Slider {...settingsNav1}>
                          {reportDetailData.images.map((v: any, k: number) => {
                            return (
                              <div onClick={openImageSlider} key={k}>
                                <div className="img-box detail-slide">
                                  {v.ext === "mp4" || v.ext === "mov" ? (
                                    <VideoBox
                                      controls
                                      vimeoId={v.img_url}
                                    />
                                  ) : (
                                    <img src={imageServerIp + v.img_url} alt={v.img_name} />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </Slider>
                      </ImgSlide>
                    )}
                  </>
                )}
              </ImageSection>

              {/* 상세 정보 영역 */}
              <InfoSection $isMobile={isMobile}>
                <Box pd={isMobile ? "40px 0" : "24px"}>
                  <TitleBox $isMobile={isMobile}>
                    <p className="title">{reportDetailData ? reportDetailData.company_name : ""}</p>
                    <p className="date">{reportDetailData ? moment(reportDetailData.completion_date).format("YYYY.MM.DD HH:mm") : ""}</p>
                  </TitleBox>
                  <ContentBox $isMobile={isMobile}>{reportDetailData ? reportDetailData.completion_contents : ""}</ContentBox>
                </Box>

                {deleteYn !== "Y" && (
                  <>
                    {userType === "E" && progress !== "ASC_100" && progress !== "ASC_70" ? (
                      <BottomFlex $isMobile={isMobile}>
                        {!isRetry && (
                          <ButtonBox>
                            <Button variant="outlined" br="round" color="secondary" onClick={() => setState({ ...state, isRetry: isRetry ? false : true })}>
                              보완 요청
                            </Button>
                            <Button variant="contained" br="round" onClick={() => setState({ ...state, isComplete: true })}>
                              확인
                            </Button>
                          </ButtonBox>
                        )}

                        {isRetry && (
                          <>
                            <RejectionReason
                              onChange={(e) => onChange(e)}
                              id="rejectReason"
                              title="보완요청 사유"
                              placeholder="보완요청 사유입니다."
                            />
                            <ButtonBox>
                              <Button variant="contained" br="round" onClick={() => reCompleteBtn()}>
                                완료보고 재요청
                              </Button>
                            </ButtonBox>
                          </>
                        )}
                      </BottomFlex>
                    ) : (
                      <>
                        {userType === "M" && progress === "ASC_70" && (
                          <BottomFlex $isMobile={isMobile}>
                            <ButtonBox>
                              <Button variant="contained" br="round" onClick={() => navigate("/report-register/" + as_idx)}>
                                완료보고 수정
                              </Button>
                            </ButtonBox>
                          </BottomFlex>
                        )}
                      </>
                    )}
                  </>
                )}
              </InfoSection>
            </MainContentArea>
          </FlexWrap>
        </ResponsiveContainer>
      </Body>

      <ImageModal ref={imageModalRef}>
        <ModalImgSlide>
          <StyledSlider {...settingsNav2}>
            {reportDetailAPI.isSuccess &&
              reportDetailAPI.data.images.length > 0 &&
              reportDetailAPI.data.images.map((v: any, k: number) => {
                return (
                  <div className="img-box" key={k}>
                    {v.ext === "mp4" || v.ext === "mov" ? (
                      <VideoBox
                        controls
                        playAllow
                        vimeoId={v.img_url}
                        // src={imageServerIp + v.img_url}
                      />
                    ) : (
                      <img src={imageServerIp + v.img_url} alt={v.img_name} />
                    )}
                  </div>
                );
              })}
          </StyledSlider>
        </ModalImgSlide>
      </ImageModal>
    </>
  );
}

// PC/모바일 반응형 컨테이너
const ResponsiveContainer = styled.div<{ $isMobile: boolean }>`
  padding: ${({ $isMobile }) => ($isMobile ? "0" : "24px")};
`;

// 메인 콘텐츠 영역 (PC: 2컬럼, 모바일: 1컬럼)
const MainContentArea = styled.div<{ $isMobile: boolean }>`
  display: ${({ $isMobile }) => ($isMobile ? "block" : "flex")};
  gap: ${({ $isMobile }) => ($isMobile ? "0" : "32px")};
  align-items: stretch;
`;

// 이미지 섹션
const ImageSection = styled.div<{ $isMobile: boolean }>`
  ${({ $isMobile }) =>
    $isMobile
      ? `
    width: 100%;
  `
      : `
    flex: 1;
    min-width: 0;
  `}
`;

// 상세 정보 섹션
const InfoSection = styled.div<{ $isMobile: boolean }>`
  ${({ $isMobile }) =>
    $isMobile
      ? `
    width: 100%;
  `
      : `
    flex: 1;
    min-width: 0;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
  `}
`;

const FlexWrap = styled.div<{ $isMobile: boolean }>`
  ${({ theme }) => theme.flexBox.flexMixin("column", "space-between", "stretch")}
  height: auto;
  min-height: 100%;
`;

const TitleBox = styled.div<{ $isMobile: boolean }>`
  padding: ${({ $isMobile }) => ($isMobile ? "0 45px" : "0")};
  margin-bottom: ${({ $isMobile }) => ($isMobile ? "50px" : "24px")};
  .title {
    font-weight: 700;
    font-size: 18px;
  }
  .date {
    margin-top: 10px;
    color: #818181;
    font-size: 14px;
    font-family: "Poppins";
    font-weight: 400;
  }
`;

const ImgSlide = styled.div<{ $isMobile: boolean }>`
  overflow: hidden;
  height: auto;
  background-color: #fff;

  ${({ $isMobile }) =>
    !$isMobile &&
    `
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    height: 100%;
    position: relative;

    .img-box.detail-slide {
      height: 480px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f5f5f5;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    ${/* 화살표 위치 조정 */ ""}
    .slick-prev {
      left: 12px;
      z-index: 10;
    }

    .slick-next {
      right: 12px;
      z-index: 10;
    }

    ${/* dots 위치 조정 및 스타일 - 이미지 위에 표시 */ ""}
    .slick-dots {
      bottom: 16px;
      z-index: 10;
    }

    .slick-dots li button:before {
      border: none;
      background-color: rgba(255, 255, 255, 0.5);
    }

    .slick-dots li.slick-active button:before {
      border: none;
      background-color: #fff;
    }

    ${/* 슬라이더 영역 이미지 꽉 차게 */ ""}
    .slick-slider,
    .slick-list,
    .slick-track,
    .slick-slide,
    .slick-slide > div {
      height: 100%;
    }

    .slick-slide > div > div {
      height: 100%;
    }
  `}
`;

const ContentBox = styled.div<{ $isMobile: boolean }>`
  padding: ${({ $isMobile }) => ($isMobile ? "20px 45px" : "20px 0")};
  color: #001827;
  font-size: 16px;
  line-height: 24px;
`;

const BottomFlex = styled.div<{ $isMobile: boolean }>`
  padding: ${({ $isMobile }) => ($isMobile ? "55px 45px" : "24px")};
  border-top: 2px dashed #aaa;
  background-color: ${({ theme }) => theme.color.bgPrimary};

  ${({ $isMobile }) =>
    !$isMobile &&
    `
    border-radius: 0 0 8px 8px;
    margin-top: auto;
  `}

  > dl {
    padding: 0 0 40px;
    textarea {
      background-color: #fff;
    }
  }
`;

const ButtonBox = styled.div`
  ${({ theme }) => theme.flexBox.flexMixin()}
  > button {
    margin-right: 10px;
    &:last-child {
      margin-right: 0;
    }
  }
`;

const ModalImgSlide = styled.div`
  height: 100%;
  background: #ccc;
`;

const StyledSlider = styled(Slider)`
  .slick-track {
    height: 100%;
  }
  .slick-slide {
    height: 100%;
    ${({ theme }) => theme.flexBox.flexMixin("column", "center", "center")}
    > div {
      height: auto;
      overflow-y: scroll;
    }
    .img-box {
      padding-top: 0;
      > img {
        position: initial;
        top: 0;
        left: 0;
        transform: none;
      }
    }
  }
  .slick-list {
    background: rgba(0, 0, 0, 0.5);
  }
`;

export default reportDetail;
